import { Interpolation, Theme } from '@emotion/react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Popover,
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Form,
  ModalFooter
} from '@anytag/shared-atoms';
import { cn } from '@anytag/shared-utils';
import { FILTER_SECTIONS } from './types';

export interface MoreFilterButtonProps {
  count: number;
  onClear: () => void;
  onSubmit?: () => void;
  items: {
    node: ReactNode;
    value: FILTER_SECTIONS;
    title?: string;
    css?: Interpolation<Theme>;
  }[];
  defaultOpened?: FILTER_SECTIONS[];
  className?: string;
}

export const MoreFilterPopover = ({
  count,
  defaultOpened,
  items,
  onClear,
  onSubmit,
  className
}: MoreFilterButtonProps) => {
  const { t } = useTranslation();
  const active = !!count;

  const [accordionValue, setAccordionValue] = useState<FILTER_SECTIONS[]>(defaultOpened || []);

  const handleSave = (closePopover: () => void) => {
    onSubmit?.();
    closePopover();
  };

  return (
    <Popover
      side="bottom"
      align="end"
      sideOffset={-5}
      withBorder
      target={({ togglePopover }) => (
        <Form.Field className={cn('flex-none', className)}>
          <Button
            className="items-center pr-2"
            onClick={togglePopover}
            title={t('More Filter')}
            prefixIcon={{ icon: 'filter', size: '18px' }}
            variant="solid" // TODO: add active style (seems different in the new design)
          >
            <span
              className={cn(
                'flex size-5 flex-none items-center justify-center overflow-hidden rounded-full bg-white text-primary',
                active ? 'visible' : 'invisible'
              )}
            >
              {count}
            </span>
          </Button>
        </Form.Field>
      )}
    >
      {({ closePopover }) => (
        <div className="w-[320px] rounded-b-sm bg-light-gray">
          <Accordion
            type="multiple"
            value={accordionValue}
            onValueChange={(v) => setAccordionValue(v as FILTER_SECTIONS[])}
            className="max-h-[75vh] overflow-y-auto" // max-h is for scroll when accordion's height is more than the screen
          >
            {items.map(({ value, node, title }) => (
              <AccordionItem key={value} value={value} className="last:border-b">
                <AccordionTrigger open={accordionValue.includes(value)}>{t(title || 'More Filter')}</AccordionTrigger>
                {/* Need to wrap `form-provider filter-form-provider` again because this component render under portal which losing the Form.Provider context */}
                <AccordionContent className="form-provider filter-form-provider">{node}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <ModalFooter
            className="mt-0"
            cancelButton={{
              title: t('Button.Clear All'),
              onClick: onClear
            }}
            submitButton={{
              title: t('Button.Save'),
              onClick: () => handleSave(closePopover)
            }}
          />
        </div>
      )}
    </Popover>
  );
};
